import { getListName } from 'analytics/enhanced-ecommerce'
import { productClickObject } from 'analytics/eventDefinitions'
import { productClick } from 'analytics/enhanced-ecommerce'
import trackClientEvent from 'analytics/snowplow-events/clientEvents'
import { OfferListEvents } from 'components/OfferList/v2/OfferListEventsContext'
import { OFFER_TYPE_HOTEL, PRODUCT_TYPE_TOUR, PRODUCT_TYPE_ULTRALUX } from 'constants/offer'
import uuidV4 from 'lib/uuidV4'
import { useCallback, useMemo } from 'react'
import { trackSearchEvent } from 'analytics/snowplow-events/search'
import { SearchEventData } from 'contexts/GlobalSearch/GlobalSearchTracking'

type HomepageListType = 'tour' | 'hotel' | 'ultralux_hotel' | 'hero' | 'trending'

type ClientActionEventData = {
  subject: string;
  action: string;
}

type ProductClickEventData = {
  offer: App.AnyOffer;
  position: number;
}

type ClientActionEvent = {
  action: OfferListEvents.clientAction;
  data: ClientActionEventData;
}

type ProductClickEvent = {
  action: OfferListEvents.productClick;
  data: ProductClickEventData;
}

type Event = ClientActionEvent | ProductClickEvent
type Data = ClientActionEventData | ProductClickEventData

function buildEvent(action: OfferListEvents, data: Data): Event | null {
  if (action === OfferListEvents.productClick && 'offer' in data) {
    return { action, data }
  } else if (action === OfferListEvents.clientAction && 'subject' in data) {
    return { action, data }
  }
  return null
}

function useHomepageAnalytics(version: 'homepage' | 'old-homepage', currency: string) {
  const listParams = useMemo(() => {
    return {
      [PRODUCT_TYPE_TOUR]: { listId: uuidV4(), listName: getListName('OfferList', version, 'tour-carousel') },
      [OFFER_TYPE_HOTEL]: { listId: uuidV4(), listName: getListName('OfferList', version, 'hotel-carousel') },
      [PRODUCT_TYPE_ULTRALUX]: { listId: uuidV4(), listName: getListName('OfferList', version, 'ultralux-carousel') },
      hero: { listId: uuidV4(), listName: getListName('OfferList', version, 'hero-carousel') },
      trending: { listId: uuidV4(), listName: `${version}-trending-destinations` },
    }
  }, [version])

  const onListEvent = useCallback((productType: HomepageListType, event: Event) => {
    const params = listParams[productType]
    if (params) {
      const { action, data } = event
      if (action === OfferListEvents.productClick) {
        const obj = productClickObject(data.offer, data.position)
        productClick(currency, obj, params)
      } else if (action === OfferListEvents.clientAction) {
        trackClientEvent(data.subject, data.action, params.listName, 'interaction')
      }
    }
  }, [currency, listParams])

  const onTourListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) { onListEvent(PRODUCT_TYPE_TOUR, event) }
  }, [onListEvent])

  const onHotelListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) { onListEvent(OFFER_TYPE_HOTEL, event) }
  }, [onListEvent])

  const onUltraLuxListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) { onListEvent(PRODUCT_TYPE_ULTRALUX, event) }
  }, [onListEvent])

  const onHeroListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) { onListEvent('hero', event) }
  }, [onListEvent])

  const onSearchEvent = useCallback((subject: string, data: SearchEventData) => {
    trackSearchEvent(subject, version, data)
  }, [version])

  const onTrendingDestinationsListEvent = useCallback((action: OfferListEvents, data: Data) => {
    const event = buildEvent(action, data)
    if (event) { onListEvent('trending', event) }
  }, [onListEvent])

  return {
    onTourListEvent,
    onHotelListEvent,
    onUltraLuxListEvent,
    onHeroListEvent,
    onSearchEvent,
    onTrendingDestinationsListEvent,
  }
}

export default useHomepageAnalytics
